import { useCallback, useRef } from "react";
import { PlaygroundType, type Resource } from "@/services/api/widget/types";
import { usePlayground } from "@/context/PlaygroundContext";
import type { ExtendedAssetInfo } from "./useAssetManager";
import type { ChartSettings } from "./usePlaygroundSettings";
import { mapPeriodToApiTimeRange } from "../periodConfig";

interface PlaygroundDataOptions {
  defaultResourceType?: "product" | "portfolio" | "benchmark" | "index";
}

export const usePlaygroundData = ({
  defaultResourceType = "product",
}: PlaygroundDataOptions = {}) => {
  const { config, data, assetStats, isLoading, error, accessDeniedIds, fetchData } =
    usePlayground();

  // Add refs for tracking fetch state
  const fetchInProgressRef = useRef(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to fetch playground data based on selected assets
  const fetchPlaygroundData = useCallback(
    async (
      assetIds: string[],
      assetMetadata: Record<string, ExtendedAssetInfo>,
      chartSettings: Partial<ChartSettings>
    ) => {
      // If a fetch is already in progress, don't start a new one
      if (fetchInProgressRef.current) {
        return;
      }

      // If there are no assets, don't fetch
      if (!assetIds || !assetIds.length) {
        return;
      }

      // StatisticsTable and CorrelationMatrix handle their own data fetching - skip context fetch
      if (
        config.type === PlaygroundType.StatisticsTable ||
        config.type === PlaygroundType.CorrelationMatrix
      ) {
        return;
      }

      if (
        config.type === PlaygroundType.ExposureOverTime &&
        assetIds.length !== 1
      ) {
        return;
      }

      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      // Set a timeout for debouncing
      fetchTimeoutRef.current = setTimeout(async () => {
        fetchInProgressRef.current = true;

        try {
          // Filter out invalid asset IDs
          const validAssetIds = assetIds.filter(
            (id) =>
              id !== undefined &&
              id !== null &&
              typeof id === "string" &&
              id.trim() !== ""
          );

          // Convert assetIds to resources using metadata
          const resources = validAssetIds.map((id) => {
            const meta = assetMetadata[id];
            if (meta) {
              return {
                resource_type: meta.resourceType as
                  | "product"
                  | "portfolio"
                  | "benchmark"
                  | "index",
                resource_id: meta.resourceId,
                stream_type: "default", // Always use default to let backend resolve appropriate stream type
              } satisfies Resource;
            }

            // Fallback to default values if no metadata
            return {
              resource_type: defaultResourceType,
              resource_id: id,
              stream_type: "default", // Let the backend resolve the appropriate default
            } satisfies Resource;
          });

          // Extract relevant chart settings
          const {
            period,
            customDateRange,
            frequency,
            statistic,
            windowSize,
            metricFieldPath,
            displayMode,
            rebaseValue,
            exposureType,
            combineVariants,
            exposureDisplayMode,
            xStatistic,
            yStatistic,
            xLabel,
            yLabel,
            extendIncompleteWithProxy,
          } = chartSettings;

          const timeRange = mapPeriodToApiTimeRange(period, customDateRange);

          // For Underwater type, force the statistic to be "underwater"
          const effectiveStatistic = 
            config.type === PlaygroundType.Underwater 
              ? "underwater" 
              : (statistic || "volatility");

          // Build fetch options based on chart settings
          const fetchOptions = {
            frequency: frequency || "monthly",
            statistic: effectiveStatistic,
            windowSize: windowSize || 30,
            timeRange,

            // Add type-specific options
            ...(config.type === PlaygroundType.PerformanceHistory && {
              displayMode: displayMode || "performance",
              rebaseValue: rebaseValue || 100,
              extendIncompleteWithProxy: extendIncompleteWithProxy ?? false,
            }),
            ...((config.type === PlaygroundType.RollingStats ||
              config.type === PlaygroundType.Underwater) && {
              extendIncompleteWithProxy: extendIncompleteWithProxy ?? false,
            }),
            ...(config.type === PlaygroundType.Exposure ||
            config.type === PlaygroundType.ExposureColumns
              ? {
                  exposureType: exposureType || "asset_allocation",
                  combineVariants: combineVariants ?? true,
                }
              : {}),
            ...(config.type === PlaygroundType.ExposureOverTime
              ? {
                  exposureType: exposureType || "asset_allocation",
                  exposureDisplayMode: exposureDisplayMode || "scaled_weights",
                  frequency: frequency || "quarterly",
                }
              : {}),
            ...(config.type === PlaygroundType.ScatterChart && {
              xStatistic: xStatistic || "generic-stat-annualized-3y",
              yStatistic: yStatistic || "generic-stat-volatility-36m-m",
              xLabel: xLabel || "3y Annualised Performance",
              yLabel: yLabel || "3y Annualised Volatility",
              includeLabels: true,
            }),
            ...(config.type === PlaygroundType.MetricsOverTime && {
              metricFieldPath:
                metricFieldPath ||
                "calculated_fields.statistics.saltus-stat-volatility-3y-m-ex-ante.value",
            }),
          };

          // Fetch the data using the context's fetchData method
          await fetchData(resources, fetchOptions);
        } catch (error) {
          console.error("[usePlaygroundData] Error fetching data:", error);
        } finally {
          fetchInProgressRef.current = false;
          fetchTimeoutRef.current = null;
        }
      }, 300); // 300ms debounce
    },
    [config.type, fetchData, defaultResourceType]
  );

  return {
    playgroundType: config.type,
    data,
    assetStats,
    isLoading,
    error,
    accessDeniedIds,
    fetchPlaygroundData,
  };
};
